import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { powerTypesAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { PowerType } from '../../types';

interface FormData {
  powerTypeName: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
}

const PowerTypes = () => {
  const [items, setItems] = useState<PowerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<PowerType | null>(null);
  const [formData, setFormData] = useState<FormData>({ powerTypeName: '', description: '', displayOrder: 0, isActive: true });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try { const response = await powerTypesAPI.getAll(); setItems(response.data); setError(''); }
    catch { setError('Failed to load power types'); } finally { setLoading(false); }
  };

  const handleOpenDialog = (item: PowerType | null = null) => {
    if (item) {
      setEditing(item);
      setFormData({ powerTypeName: item.PowerTypeName, description: item.Description || '', displayOrder: item.DisplayOrder, isActive: !!item.IsActive });
    } else {
      setEditing(null);
      setFormData({ powerTypeName: '', description: '', displayOrder: 0, isActive: true });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditing(null); };

  const handleSubmit = async () => {
    try {
      if (editing) { await powerTypesAPI.update(editing.PowerTypeId, formData); }
      else { await powerTypesAPI.create(formData); }
      fetchData(); handleCloseDialog(); setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to save power type');
    }
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await powerTypesAPI.delete(id); fetchData(); } catch { setError('Failed to delete power type'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Power Types</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>Add Power Type</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Name</TableCell><TableCell>Description</TableCell><TableCell>Display Order</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.PowerTypeId}>
                <TableCell>{item.PowerTypeName}</TableCell><TableCell>{item.Description || '-'}</TableCell>
                <TableCell>{item.DisplayOrder}</TableCell><TableCell>{item.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(item)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => setConfirmDialog({ open: true, id: item.PowerTypeId })} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Power Type' : 'Add Power Type'}</DialogTitle>
        <DialogContent>
          <TextField size="small" autoFocus margin="dense" label="Name" fullWidth value={formData.powerTypeName} onChange={(e) => setFormData({ ...formData, powerTypeName: e.target.value })} required />
          <TextField size="small" margin="dense" label="Description" fullWidth multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <TextField size="small" margin="dense" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.powerTypeName}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={confirmDialog.open} title="Deactivate Power Type" message="Are you sure you want to deactivate this power type?" confirmLabel="Deactivate" confirmColor="error" onConfirm={handleDeleteConfirm} onCancel={() => setConfirmDialog({ open: false, id: 0 })} />
    </Box>
  );
};

export default PowerTypes;
